import { useEffect, useState, type ReactNode } from "react";
import { Download, Eye, Link as LinkIcon, Loader2, X } from "lucide-react";
import { request } from "../lib/api";
import { errorMessage } from "./ui";

type Content = Record<string, unknown>;

interface PreviewItem {
  id: string;
  type: string;
  title: string;
  description: string;
  position: number;
  required: boolean;
  status: string;
  estimated_minutes: number | null;
  content: Content | null;
}

interface PreviewModule {
  id: string;
  title: string;
  description: string;
  items: PreviewItem[];
}

const EMBED_ALLOWED_PROVIDERS = new Set(["youtube", "vimeo", "google_slides", "google_forms", "google_drive"]);

const fieldClass =
  "w-full rounded-md border border-border bg-surface-muted p-3 text-sm text-content-disabled";

export function ModulePreviewModal({ moduleId, onClose }: { moduleId: string; onClose: () => void }) {
  const [module, setModule] = useState<PreviewModule | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    request<PreviewModule>(`/api/admin/modules/${moduleId}/preview`)
      .then(setModule)
      .catch((cause) => setError(errorMessage(cause)));
  }, [moduleId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Teacher preview"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-border bg-surface text-left text-content-primary shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-center gap-2 border-b border-border bg-surface-muted px-4 py-3">
          <Eye size={16} className="text-content-secondary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
            Teacher preview
          </span>
          <button
            type="button"
            className="ml-auto rounded-md p-1 text-content-secondary hover:bg-surface hover:text-content-primary"
            onClick={onClose}
            aria-label="Close preview"
          >
            <X size={18} />
          </button>
        </header>
        <div className="overflow-y-auto px-5 py-5">
          {error ? (
            <p className="text-sm text-danger">{error}</p>
          ) : !module ? (
            <p className="flex items-center gap-2 text-sm text-content-secondary">
              <Loader2 size={16} className="animate-spin" />
              Loading preview…
            </p>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-content-primary">{module.title}</h2>
              {module.description && (
                <p className="mt-1 text-sm text-content-secondary">{module.description}</p>
              )}
              <p className="mt-3 rounded-md bg-surface-muted px-3 py-2 text-xs text-content-secondary">
                Read-only. Nothing you do here is saved.
              </p>
              {!module.items.length ? (
                <p className="mt-6 text-sm text-content-secondary">This module has no content yet.</p>
              ) : (
                <ol className="mt-6 grid gap-6">
                  {module.items.map((item, index) => (
                    <li key={item.id} className="grid gap-3 border-t border-border pt-5 first:border-0 first:pt-0">
                      <div>
                        <div className="flex items-center gap-2 text-xs text-content-secondary">
                          <span>
                            {index + 1}. {item.type.replaceAll("_", " ")}
                          </span>
                          {item.estimated_minutes ? <span>· {item.estimated_minutes} min</span> : null}
                          {item.status !== "published" && (
                            <span className="ml-auto rounded bg-warning-bg px-1.5 py-0.5 font-medium text-warning-text">
                              {item.status} · hidden from teachers
                            </span>
                          )}
                        </div>
                        <h3 className="mt-1 text-base font-semibold text-content-primary">{item.title}</h3>
                        {item.description && (
                          <p className="mt-1 text-sm text-content-secondary">{item.description}</p>
                        )}
                      </div>
                      <ItemBody item={item} />
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function str(value: unknown) {
  return typeof value === "string" ? value : "";
}

function list(value: unknown): Content[] {
  return Array.isArray(value) ? (value as Content[]) : [];
}

function ItemBody({ item }: { item: PreviewItem }) {
  const c = item.content || {};
  const s = (key: string) => str(c[key]);
  switch (item.type) {
    case "video":
      return s("source") === "uploaded" || !s("source") ? (
        <video controls src={s("video_url")} poster={s("poster_url") || undefined} className="w-full rounded-md" />
      ) : (
        <VideoEmbed provider={s("source")} url={s("video_url")} />
      );
    case "audio":
      return (
        <>
          {s("cover_image_url") && <img src={s("cover_image_url")} alt="" className="w-full rounded-md" />}
          <audio controls src={s("audio_url")} className="w-full" />
        </>
      );
    case "audio_slides":
      return (
        <>
          {list(c.slides).map((slide, i) => (
            <img key={str(slide.id) || i} src={str(slide.image_url)} alt={str(slide.title) || `Slide ${i + 1}`} className="w-full rounded-md" />
          ))}
          <audio controls src={s("audio_url")} className="w-full" />
        </>
      );
    case "text":
      // body is sanitized server-side (bluemonday) before storage.
      return <div className="text-sm leading-relaxed text-content-primary" dangerouslySetInnerHTML={{ __html: s("body") }} />;
    case "image":
    case "gallery":
      return (
        <>
          {list(c.images).map((img, i) => (
            <figure key={str(img.id) || i} className="m-0">
              <img src={str(img.url)} alt={str(img.alt_text) || str(img.caption)} className="w-full rounded-md" loading="lazy" />
              {str(img.caption) && <figcaption className="mt-1 text-xs text-content-secondary">{str(img.caption)}</figcaption>}
            </figure>
          ))}
        </>
      );
    case "slides":
      return (
        <>
          <div className="aspect-[4/3] w-full overflow-hidden rounded-md border border-border bg-surface-muted">
            <iframe
              className="h-full w-full"
              title={s("title") || "Slides"}
              src={s("file_type") === "pdf" ? s("file_url") : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(s("file_url"))}`}
            />
          </div>
          <FileLink href={s("file_url")} label="Open slides" />
        </>
      );
    case "downloadable":
      return <FileLink href={s("file_url")} label={`Download ${s("file_name") || "file"}`} />;
    case "resource_pack":
      return (
        <div className="grid gap-2">
          {list(c.files).map((file, i) => (
            <FileLink key={str(file.id) || i} href={str(file.file_url)} label={str(file.file_name)} />
          ))}
        </div>
      );
    case "external_link":
      return (
        <a className={linkClass} href={s("url")} target="_blank" rel="noopener noreferrer">
          <LinkIcon size={16} />
          {s("label") || s("url")}
        </a>
      );
    case "embed":
      return EMBED_ALLOWED_PROVIDERS.has(s("provider")) ? (
        <div className="aspect-video w-full overflow-hidden rounded-md border border-border">
          <iframe
            className="h-full w-full"
            src={s("embed_url")}
            title={s("title") || "Embedded content"}
            sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
          />
        </div>
      ) : (
        <p className="text-sm text-danger">This embed provider is not allowed to render.</p>
      );
    case "practical_activity":
      return (
        <>
          <div className="text-sm leading-relaxed text-content-primary" dangerouslySetInnerHTML={{ __html: s("instructions") }} />
          {list(c.files).map((file, i) => (
            <FileLink key={str(file.id) || i} href={str(file.file_url)} label={str(file.file_name)} />
          ))}
          {(s("response_type") === "short_text" || s("response_type") === "long_text") && <ResponseBox />}
        </>
      );
    case "reflection":
      return (
        <>
          <p className="text-sm leading-relaxed text-content-primary">{s("prompt")}</p>
          <ResponseBox />
        </>
      );
    case "quiz":
      return (
        <div className="grid gap-4">
          {typeof c.pass_mark === "number" && <p className="text-xs text-content-secondary">Pass mark: {c.pass_mark}%</p>}
          {list(c.questions).map((question, i, all) =>
            question.type === "section" ? (
              <h3 key={str(question.id) || i} className="mt-2 whitespace-pre-line text-base font-semibold text-content-primary">
                {str(question.text)}
              </h3>
            ) : (
              <Question
                key={str(question.id) || i}
                // Headings are not numbered.
                number={all.slice(0, i + 1).filter((entry) => entry.type !== "section").length}
                question={question}
              />
            )
          )}
        </div>
      );
    case "form":
      return (
        <div className="grid gap-4">
          {list(c.fields).map((field, i) => (
            <FormField key={str(field.id) || i} field={field} />
          ))}
        </div>
      );
    default:
      return <p className="text-sm text-content-secondary">Preview not available for this content type.</p>;
  }
}

const linkClass =
  "flex items-center gap-2 rounded-md border border-border p-3 text-sm font-medium text-interactive hover:bg-surface-muted";

function FileLink({ href, label }: { href: string; label: ReactNode }) {
  return (
    <a className={linkClass} href={href} target="_blank" rel="noreferrer">
      <Download size={16} />
      {label}
    </a>
  );
}

function ResponseBox() {
  return <textarea className={`${fieldClass} min-h-20 resize-none`} placeholder="Teacher types their response here" disabled />;
}

function Question({ number, question }: { number: number; question: Content }) {
  const options = list(question.options);
  return (
    <fieldset className="grid gap-2" disabled>
      <legend className="mb-1 text-sm font-medium text-content-primary">
        {number}. {str(question.text)}
      </legend>
      {str(question.image_url) && <img src={str(question.image_url)} alt="" className="w-full rounded-md" />}
      {options.length ? (
        options.map((option, i) => (
          <label key={str(option.id) || i} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-content-primary">
            <input type={question.type === "multiple_choice" ? "checkbox" : "radio"} name={str(question.id)} />
            {str(option.text)}
          </label>
        ))
      ) : (
        <input className={fieldClass} placeholder="Answer" />
      )}
    </fieldset>
  );
}

function FormField({ field }: { field: Content }) {
  const options = list(field.options);
  return (
    <fieldset className="grid gap-2" disabled>
      <legend className="mb-1 text-sm font-medium text-content-primary">
        {str(field.label)}
        {field.required === true && <span className="text-danger"> *</span>}
      </legend>
      {str(field.help_text) && <p className="text-xs text-content-secondary">{str(field.help_text)}</p>}
      {field.type === "informational" ? null : field.type === "dropdown" ? (
        <select className={fieldClass}>
          <option>Select…</option>
          {options.map((option, i) => (
            <option key={str(option.id) || i}>{str(option.text)}</option>
          ))}
        </select>
      ) : options.length ? (
        options.map((option, i) => (
          <label key={str(option.id) || i} className="flex items-center gap-2 text-sm text-content-primary">
            <input type={field.type === "checklist" ? "checkbox" : "radio"} name={str(field.id)} />
            {str(option.text)}
          </label>
        ))
      ) : field.type === "long_text" ? (
        <textarea className={`${fieldClass} min-h-20 resize-none`} />
      ) : (
        <input className={fieldClass} />
      )}
    </fieldset>
  );
}

function VideoEmbed({ provider, url }: { provider: string; url: string }) {
  const embedURL = toEmbedURL(provider, url || "");
  if (!embedURL) return <p className="text-sm text-danger">Unsupported video URL.</p>;
  return (
    <div className="aspect-video w-full overflow-hidden rounded-md bg-black">
      <iframe
        className="h-full w-full"
        src={embedURL}
        title="Video"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allowFullScreen
      />
    </div>
  );
}

function toEmbedURL(provider: string, url: string) {
  if (provider === "youtube") {
    const match = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  }
  if (provider === "vimeo") {
    const match = url.match(/vimeo\.com\/(\d+)/);
    return match ? `https://player.vimeo.com/video/${match[1]}` : null;
  }
  return null;
}
